<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { Trash, Download } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableDate, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useSurveyStore } from "@/stores/survey/survey";
  import { useMailingListsStore } from "@/stores/survey/mailing/mailingLists";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import type { MailingList } from "@/types";

  const mailingListsStore = useMailingListsStore();
  const { mailingLists, mailingListsCount, currentPage } = storeToRefs(mailingListsStore);
  const { changePage, removeMailingList, exportMailingList } = mailingListsStore;
  const { token: surveyToken, canWriteSurvey } = storeToRefs(useSurveyStore());
  const { isSuperAdmin } = storeToRefs(useCurrentUserStore());
  const { openRow } = useRowNavigation();

  const columns: DataTableColumn<MailingList>[] = [
    { key: "name", label: "List" },
    { key: "contacts", label: "Contacts", width: 120 },
    { key: "date", label: "Date", width: 90 },
  ];

  const actions = [{ isDelete: true, function: removeMailingList, msgConfirm: "this list", text: "delete", icon: Trash }];

  const isExpired = (list: MailingList) => Date.parse(list.expired_at) < Date.now();
  const isExportable = (list: MailingList) => list.contacts_count > 0 && (canWriteSurvey.value || isSuperAdmin.value);
  const editRoute = (list: MailingList) => `/surveys/${surveyToken.value}/mailing/lists/${list.token}/edit`;
</script>

<template>
  <IndexTable
    :items="mailingLists"
    :columns="columns"
    :current-page="currentPage"
    :total="mailingListsCount"
    actions-class="w-[150px]"
    item-key="token"
    empty-message="No list yet."
    @row-click="(list, event) => openRow(editRoute(list), event)"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name">
        <Badge
          v-if="item.shared"
          variant="info"
          class="text-2xs">
          account level
        </Badge>
        <Badge
          v-if="isExpired(item)"
          variant="neutral"
          class="text-2xs">
          expired
        </Badge>
      </IndexTableName>
    </template>
    <template #cell-contacts="{ item }">
      <Tooltip
        v-if="!isExpired(item)"
        content="The removal of duplicate contacts may alter the total count, resulting in a different number than expected">
        <span class="tabular-nums">{{ item.contacts_count?.toLocaleString() }}</span>
      </Tooltip>
      <IndexTableEmpty v-else />
    </template>
    <template #cell-date="{ item }">
      <IndexTableDate :date="item.created_at" />
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <Button
          v-if="isExportable(item)"
          variant="ghost"
          size="sm"
          class="text-primary"
          @click="exportMailingList(item.token)">
          <Download />
          Export
        </Button>
        <ActionsDropdown
          :actions="actions"
          :token="item.token" />
      </div>
    </template>
  </IndexTable>
</template>
